"use client"

import { useState, useEffect, useCallback } from "react"
import { driver } from "driver.js"
import "driver.js/dist/driver.css"
import { renderToString } from "react-dom/server"
import React from "react"
import { 
  Star, 
  Search, 
  UserPlus, 
  Tag, 
  RefreshCw, 
  Settings, 
  BarChart3, 
  Laptop, 
  Pin, 
  Edit, 
  Printer,
  ChevronRight,
  Wifi,
  PartyPopper,
  CheckCircle
} from "lucide-react"

// Helper function to create icon HTML
const createIconHtml = (Icon: React.ComponentType<any>, className = "h-5 w-5 inline-block mr-2 text-blue-600") => {
  return renderToString(React.createElement(Icon, { className }))
}

export const useTourGuide = () => {
  const [driverObj, setDriverObj] = useState<any>(null)
  const [isActive, setIsActive] = useState(false)

  // Initialize driver on component mount
  useEffect(() => {
    const driverInstance = driver({
      showProgress: true,
      animate: true,
      showButtons: ["next", "previous", "close"],
      steps: [],
      onDestroyStarted: () => {
        console.log('Tour destroy started');
        setIsActive(false);
        setTimeout(() => driverInstance.destroy(), 0);
      },
      onDestroyed: () => {
        console.log('Tour destroyed');
        setIsActive(false);
      },
      popoverClass: "driverjs-theme",
    })
    setDriverObj(driverInstance)

    return () => {
      if (driverInstance) {
        driverInstance.destroy()
      }
    }
  }, [])

  const startTour = useCallback(() => {
    if (!driverObj) return

    // Define tour steps para el sistema dinámico
    const steps = [
      {
        element: "#navbar",
        popover: {
          title: `${createIconHtml(Star)}Sistema Dinámico de Gestión`,
          description: "Bienvenido al panel de gestión de usuarios. Este sistema se adapta automáticamente a diferentes esquemas de datos y funciona completamente offline. Aquí puedes ver el estado de conexión y sincronización en tiempo real.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#search-bar",
        popover: {
          title: `${createIconHtml(Search)}Búsqueda Inteligente`,
          description: "Busca usuarios por cualquier campo disponible: nombre, apellido, email, teléfono, o cualquier campo personalizado del schema. La búsqueda es instantánea y funciona incluso sin conexión.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#new-assistant-btn",
        popover: {
          title: `${createIconHtml(UserPlus)}Registro Dinámico de Usuarios`,
          description: "Registra nuevos usuarios con formularios que se adaptan automáticamente al schema del servidor. Los datos se guardan localmente y se sincronizan cuando hay conexión. Incluye validación inteligente y generación automática de códigos únicos.",
          side: "left",
          align: "start",
        },
      },
      {
        element: "#generic-label-btn",
        popover: {
          title: `${createIconHtml(Tag)}Etiquetas Genéricas`,
          description: "Crea etiquetas personalizables para casos especiales, invitados de último momento o pruebas. Puedes editar todos los campos, incluido el código QR, y generar etiquetas sin afectar la base de datos.",
          side: "left",
          align: "start",
        },
      },
      {
        element: "#refresh-btn",
        popover: {
          title: `${createIconHtml(RefreshCw)}Sincronización de Datos`,
          description: "Actualiza manualmente los datos desde el servidor. El sistema también sincroniza automáticamente en segundo plano cuando detecta conexión a internet.",
          side: "left",
          align: "start",
        },
      },
      {
        element: "#reset-btn",
        popover: {
          title: `${createIconHtml(Settings)}Reset Completo del Sistema`,
          description: "Limpia completamente los datos locales, caché y configuraciones. IMPORTANTE: Esta acción no afecta los datos del servidor, solo elimina la información guardada localmente en este dispositivo.",
          side: "left",
          align: "start",
        },
      },
      {
        element: "#table-container",
        popover: {
          title: `${createIconHtml(BarChart3)}Tabla Dinámica de Usuarios`,
          description: "Visualiza todos los usuarios con columnas que se adaptan automáticamente al schema. La tabla incluye columnas fijas para mejor navegación, paginación inteligente y ordenamiento por múltiples campos.",
          side: "top",
          align: "start",
        },
      },
      {
        element: "#select-station",
        popover: {
          title: `${createIconHtml(Laptop)}Estación de Impresión`,
          description: "Define la estación de trabajo que se utilizará para imprimir los gafetes. Cada estación está vinculada a una impresora específica, asegurando que los documentos se impriman en la ubicación física correcta.",
          side: "left",
          align: "start",
        },
      },
      {
        element: "#fixed-column",
        popover: {
          title: `${createIconHtml(Pin)}Columna Fija`,
          description: "Esta columna siempre permanece visible al hacer scroll horizontal. Muestra el campo principal definido en el schema (puede ser ID, código, folio, etc.) para facilitar la identificación de usuarios.",
          side: "right",
          align: "start",
        },
      },
      {
        element: "#edit-btn",
        popover: {
          title: `${createIconHtml(Edit)}Edición Inteligente`,
          description: "Edita usuarios con formularios que se adaptan al schema. Solo se envían los campos modificados al servidor. Incluye validación en tiempo real, detección de cambios y sincronización optimista para mejor rendimiento.",
          side: "left",
          align: "center",
        },
      },
      {
        element: "#print-btn",
        popover: {
          title: `${createIconHtml(Printer)}Impresión Personalizable`,
          description: "Genera gafetes con diseño adaptativo que se ajusta automáticamente al contenido. Puedes personalizar qué campos mostrar, editar valores en tiempo real y generar códigos QR dinámicos. La línea debajo indica si ya fue impreso.",
          side: "left",
          align: "center",
        },
      },
      {
        element: "#pagination",
        popover: {
          title: `${createIconHtml(ChevronRight)}Navegación Optimizada`,
          description: "Navega eficientemente entre páginas con información detallada de resultados. La paginación se adapta automáticamente al número total de usuarios y conserva tu posición al realizar búsquedas.",
          side: "top",
          align: "center",
        },
      },
    ]

    // Agregar steps condicionales si existen ciertos elementos
    const conditionalSteps = [
      {
        element: "#sync-status",
        popover: {
          title: `${createIconHtml(RefreshCw)}Estado de Sincronización`,
          description: "Muestra el estado actual de sincronización: 'Sincronizado' (verde), 'Pendientes' (naranja), 'Sincronizando' (amarillo). Los datos se sincronizan automáticamente en segundo plano.",
          side: "bottom",
          align: "center",
        },
        condition: () => document.querySelector("#sync-status"),
      },
      {
        element: "#network-status",
        popover: {
          title: `${createIconHtml(Wifi)}Estado de Conexión`,
          description: "Indica si estás 'En línea' (azul) o 'Sin conexión' (rojo). El sistema funciona completamente offline y sincroniza automáticamente cuando se restaura la conexión.",
          side: "bottom",
          align: "center",
        },
        condition: () => document.querySelector("#network-status"),
      },
    ]

    // Agregar steps condicionales que existan
    conditionalSteps.forEach(step => {
      if (step.condition && step.condition()) {
        steps.push(step)
      }
    })

    // Agregar step final
    steps.push({
      element: "body",
      popover: {
        title: `${createIconHtml(PartyPopper)}¡Sistema Listo!`,
        description: `Has completado el tour del sistema dinámico de gestión. Características principales: ${createIconHtml(CheckCircle, "h-4 w-4 inline text-green-600")} Funciona offline, ${createIconHtml(CheckCircle, "h-4 w-4 inline text-green-600")} Sincronización automática, ${createIconHtml(CheckCircle, "h-4 w-4 inline text-green-600")} Formularios adaptativos, ${createIconHtml(CheckCircle, "h-4 w-4 inline text-green-600")} Impresión personalizable, ${createIconHtml(CheckCircle, "h-4 w-4 inline text-green-600")} Estación de Impresión, ${createIconHtml(CheckCircle, "h-4 w-4 inline text-green-600")} Validación en tiempo real. ¡Comienza a gestionar usuarios de forma eficiente!`,
        side: "top",
        align: "center",
      },
    })

    driverObj.setSteps(steps)
    driverObj.drive()
    setIsActive(true)
  }, [driverObj])

  return { startTour, isActive }
}
